"use client";

/**
 * P11-10 · Push the stored consent choice into the third-party tags.
 *
 * This is the half of the ticket that un-breaks P9-05. GA4 Consent Mode v2
 * defaults every signal to `denied` and waits to be granted; nothing ever
 * granted them, so GA4 and the Meta Pixel loaded and then reported nothing at
 * all in production.
 *
 * It runs on **every page load**, not only when a choice is made. A returning
 * visitor who accepted last week arrives with the tags freshly defaulted to
 * denied, and without a replay they would be treated as having declined for the
 * rest of the session — the stored consent would be a record of a decision that
 * never took effect.
 *
 * Mounted inside `ThirdPartyAnalytics` so it exists only when a brand actually
 * has a tag configured; with none, this is never rendered and no globals are
 * touched.
 */
import * as React from "react";

import { currentGrants, onConsentChange, type ConsentGrants } from "./consent";

type Gtag = (...args: unknown[]) => void;
type Fbq = (...args: unknown[]) => void;

declare global {
  interface Window {
    gtag?: Gtag;
    fbq?: Fbq;
  }
}

function apply(grants: ConsentGrants): void {
  const granted = (ok: boolean) => (ok ? "granted" : "denied");

  // `gtag` is defined by the inline init script; if the tag hasn't loaded (or
  // is blocked), skip silently — consent state is meaningless without it, and
  // throwing here would take down the storefront over an analytics detail.
  window.gtag?.("consent", "update", {
    analytics_storage: granted(grants.analytics),
    ad_storage: granted(grants.marketing),
    ad_user_data: granted(grants.marketing),
    ad_personalization: granted(grants.marketing),
  });

  // Meta's equivalent is coarse — one on/off, tied to the marketing grant.
  window.fbq?.("consent", grants.marketing ? "grant" : "revoke");
}

export function ConsentBridge() {
  React.useEffect(() => {
    // The tags are `afterInteractive`, so `gtag`/`fbq` may not exist yet on the
    // first tick. A microtask is enough in practice; if it still isn't there,
    // the optional calls above no-op and the next consent change re-applies.
    const initial = currentGrants();
    apply(initial);
    const id = window.setTimeout(() => apply(initial), 0);
    const off = onConsentChange(apply);
    return () => {
      window.clearTimeout(id);
      off();
    };
  }, []);

  return null;
}
